import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/config/firebase";
import { normalizePhoneToE164 } from "utils/phoneUtils";

const COLLECTION_NAME = "clock_reminder";
const DOCUMENT_ID = "settings";

interface ClockReminderSettings {
  enabledPhoneNumbers: string[];
  enabledPopupUserIds: string[];
  updatedAt: string;
}

const defaultSettings = (): ClockReminderSettings => ({
  enabledPhoneNumbers: [],
  enabledPopupUserIds: [],
  updatedAt: new Date().toISOString(),
});

export const useClockReminderSettings = () => {
  const queryClient = useQueryClient();

  // Fetch current settings
  const {
    data: settings,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["clockReminderSettings"],
    queryFn: async () => {
      const settingsRef = doc(db, COLLECTION_NAME, DOCUMENT_ID);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data() as ClockReminderSettings;
        const enabledPhoneNumbers = [
          ...new Set(
            (data.enabledPhoneNumbers || [])
              .map((phone) => normalizePhoneToE164(phone))
              .filter((phone): phone is string => phone !== null),
          ),
        ];
        const enabledPopupUserIds = [...new Set((data.enabledPopupUserIds || []).filter(Boolean))];
        return {
          enabledPhoneNumbers,
          enabledPopupUserIds,
          updatedAt: data.updatedAt || new Date().toISOString(),
        } as ClockReminderSettings;
      }
      
      // Return default empty settings if document doesn't exist
      return defaultSettings();
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Mutation to update settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (partial: Partial<Omit<ClockReminderSettings, "updatedAt">>) => {
      const settingsRef = doc(db, COLLECTION_NAME, DOCUMENT_ID);
      const cached =
        queryClient.getQueryData<ClockReminderSettings>(["clockReminderSettings"]) || defaultSettings();
      const newSettings: ClockReminderSettings = {
        enabledPhoneNumbers: partial.enabledPhoneNumbers ?? cached.enabledPhoneNumbers,
        enabledPopupUserIds: partial.enabledPopupUserIds ?? cached.enabledPopupUserIds,
        updatedAt: new Date().toISOString(),
      };
      await setDoc(settingsRef, newSettings, { merge: true });
      return newSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clockReminderSettings"] });
    },
  });

  // Toggle a phone number's reminder status
  const togglePhoneNumber = (phoneNumber: string, enabled: boolean) => {
    const normalized = normalizePhoneToE164(phoneNumber);
    if (!normalized) return;

    const currentNumbers = settings?.enabledPhoneNumbers || [];
    const newNumbers = enabled
      ? [...new Set([...currentNumbers, normalized])]
      : currentNumbers.filter((num) => num !== normalized);
    updateSettingsMutation.mutate({ enabledPhoneNumbers: newNumbers });
  };

  const togglePopupUserId = (userId: string, enabled: boolean) => {
    const currentIds = settings?.enabledPopupUserIds || [];
    const newIds = enabled
      ? [...new Set([...currentIds, userId])]
      : currentIds.filter((id) => id !== userId);
    updateSettingsMutation.mutate({ enabledPopupUserIds: newIds });
  };

  const isPopupEnabledForUser = (userId: string | undefined): boolean =>
    Boolean(userId && settings?.enabledPopupUserIds.includes(userId));

  return {
    settings: settings || defaultSettings(),
    isLoading,
    error,
    togglePhoneNumber,
    togglePopupUserId,
    isPopupEnabledForUser,
    isUpdating: updateSettingsMutation.isPending,
  };
};

